import { lazy, Suspense } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import { ErrorBoundary } from "@/components/ErrorBoundary";

// Landing é a rota pública de entrada (LCP) — carregada imediatamente.
import Landing from "./pages/Landing";

// Tudo o mais é lazy para reduzir o JS inicial do landing.
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogMetodos = lazy(() => import("./pages/BlogMetodos"));
const ProtectedRoute = lazy(() => import("@/components/ProtectedRoute").then(m => ({ default: m.ProtectedRoute })));
const DashboardLayout = lazy(() => import("@/components/DashboardLayout").then(m => ({ default: m.DashboardLayout })));
const AdminRoute = lazy(() => import("./components/AdminRoute").then(m => ({ default: m.AdminRoute })));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Transacoes = lazy(() => import("./pages/Transacoes"));
const Categorias = lazy(() => import("./pages/Categorias"));
const Orcamentos = lazy(() => import("./pages/Orcamentos"));
const ContasAReceber = lazy(() => import("./pages/ContasAReceber"));
const Investimentos = lazy(() => import("./pages/Investimentos"));
const Relatorios = lazy(() => import("./pages/Relatorios"));
const Cartoes = lazy(() => import("./pages/Cartoes"));
const Assinatura = lazy(() => import("./pages/Assinatura"));
const Suporte = lazy(() => import("./pages/Suporte"));
const Admin = lazy(() => import("./pages/Admin"));
const Sobre = lazy(() => import("./pages/Sobre"));
const Privacidade = lazy(() => import("./pages/Privacidade"));
const Termos = lazy(() => import("./pages/Termos"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));

// QueryClient com cache agressivo: evita refetch desnecessário ao trocar de aba.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000, // 1 min — dados considerados frescos
      gcTime: 5 * 60_000, // 5 min em cache antes de descartar
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const PageFallback = () => (
  <div className="flex h-[60vh] items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <ErrorBoundary>
            <Suspense fallback={<PageFallback />}>
              <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/sobre" element={<Sobre />} />
              <Route path="/privacidade" element={<Privacidade />} />
              <Route path="/termos" element={<Termos />} />
              <Route path="/blog/planilha-vs-aplicativo-controle-financeiro" element={<BlogMetodos />} />

              <Route
                element={
                  <ProtectedRoute>
                    <DashboardLayout />
                  </ProtectedRoute>
                }
              >
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/transacoes" element={<Transacoes />} />
                <Route path="/categorias" element={<Categorias />} />
                <Route path="/orcamentos" element={<Orcamentos />} />
                <Route path="/contas-a-receber" element={<ContasAReceber />} />
                <Route path="/investimentos" element={<Investimentos />} />
                <Route path="/relatorios" element={<Relatorios />} />
                <Route path="/cartoes" element={<Cartoes />} />
                <Route path="/assinatura" element={<Assinatura />} />
                <Route path="/suporte" element={<Suporte />} />
                <Route
                  path="/admin"
                  element={
                    <AdminRoute>
                      <Admin />
                    </AdminRoute>
                  }
                />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          </ErrorBoundary>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
